import React, { useEffect } from 'react';
import { Typography, Tabs as TabsNavigation, Flex, message } from 'antd';
import requireAuth from '../components/hocs/requireAuth';
import AdminTemplate from '../layout/AdminTemplate';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ManualQueueTab } from '../features/scheduled-rendering/ManualQueueTab';
import { AutomaticQueueTab } from '../features/scheduled-rendering/AutomaticQueueTab';
import { useDispatch } from 'react-redux';
import { usePrerenderUser } from '../hooks/usePrerenderUser';
import { useTogglePauseRecachingMutation } from '../features/scheduled-rendering/RecacheQueueApiSlice';
import { prerenderUserPatch } from '../actions/prerenderUserActions';
import { RecachingPausedMessage } from '../features/scheduled-rendering/RecachingPausedMessage';

const META_TAGS = {
  title: 'Caching Queue - Prerender.io',
};

export const enum Tabs {
  ManualQueue = 'priority',
  AutomatedQueue = 'standard',
}

const TABS = [
  { key: Tabs.ManualQueue, label: 'Manual, API Queue', component: <ManualQueueTab /> },
  { key: Tabs.AutomatedQueue, label: 'Automated Queue', component: <AutomaticQueueTab /> },
];

const NewScheduledRenderingPage = () => {
  const [messageApi, messageContext] = message.useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const prerenderUser = usePrerenderUser();
  const dispatch = useDispatch();
  const [togglePauseRecaching, { error: toggleRecachingError }] = useTogglePauseRecachingMutation();

  const activeTab = React.useMemo(() => location.pathname.split('/').pop(), [location.pathname]);

  const onTabClick = (tabKey: string) => {
    // Absolute: relative targets resolve against the full splat match in react-router 7.
    navigate(`/recache-queue/${tabKey}`);
  };

  useEffect(() => {
    if (toggleRecachingError) {
      messageApi.error('Failed to resume caching. Please try again later.');
    }
  }, [toggleRecachingError]);

  return (
    <AdminTemplate metaTags={META_TAGS}>
      {messageContext}
      <div>
        <Flex vertical gap="large">
          {!prerenderUser.inProgress && prerenderUser.userPausedRecaching && (
            <RecachingPausedMessage
              onResumeCaching={() => {
                togglePauseRecaching()
                  .unwrap()
                  .then(() => {
                    dispatch(prerenderUserPatch({ userPausedRecaching: !prerenderUser.userPausedRecaching }));
                  });
              }}
            />
          )}
          <Typography.Title level={2}>Caching Queue</Typography.Title>
        </Flex>
        <Typography.Paragraph type="secondary">
          URLs prompted for caching and re-caching, either manually or automatically.
        </Typography.Paragraph>
      </div>
      <TabsNavigation activeKey={activeTab} items={TABS} onTabClick={onTabClick} />

      <Routes>
        {TABS.map(({ key, component }) => (
          <Route path={key} key={key} element={component} />
        ))}
        {/* Absolute target: a relative `to` in a splat catch-all resolves against the full
            splat match in react-router 7 and would redirect onto itself forever. */}
        <Route path="*" element={<Navigate to={`/recache-queue/${TABS[0].key}`} replace />}></Route>
      </Routes>
    </AdminTemplate>
  );
};

export default requireAuth(NewScheduledRenderingPage);
